import React, { useState } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { useStripeContext } from '../contexts/StripeContext';
import './Wallet.css';

const Wallet = () => {
  const { 
    balance, 
    transactions, 
    loading, 
    topUpWallet, 
    confirmTopUp, 
    formatBalance, 
    formatAmount, 
    getTransactionTypeDisplay 
  } = useWallet();
  
  const { stripePromise } = useStripeContext();

  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(1000); // $10.00 default
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const topUpOptions = [
    { value: 500, label: '$5.00' },
    { value: 1000, label: '$10.00' },
    { value: 2500, label: '$25.00' },
    { value: 5000, label: '$50.00' },
    { value: 10000, label: '$100.00' }
  ];

  const handleTopUp = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setError('');

    try {
      // Create payment intent
      const paymentData = await topUpWallet(topUpAmount);
      
      // Initialize Stripe using context
      const stripe = await stripePromise;
      
      if (!stripe) {
        throw new Error('Stripe not loaded');
      }

      // Confirm payment
      const { error: stripeError } = await stripe.confirmPayment({
        elements: undefined,
        confirmParams: {
          return_url: window.location.origin + '/wallet?success=true',
          payment_method_data: {
            billing_details: {
              name: 'Wallet Top-up'
            }
          }
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Confirm top-up on our server
      await confirmTopUp(paymentData.client_secret.split('_secret_')[0], topUpAmount);
      
      setShowTopUp(false);
      setTopUpAmount(1000);
    } catch (error) {
      console.error('Top-up error:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="wallet-container">
        <div className="wallet-loading">
          <div className="loading-spinner"></div>
          <p>Loading wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-container">
      <div className="wallet-header">
        <h1>My Wallet</h1>
        <div className="wallet-balance">
          <span className="balance-label">Balance:</span>
          <span className="balance-amount">${formatBalance(balance)}</span>
        </div>
      </div>

      <div className="wallet-actions">
        <button 
          className="top-up-btn"
          onClick={() => setShowTopUp(true)}
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing...' : 'Add Funds'}
        </button>
      </div>

      {showTopUp && (
        <div className="top-up-modal">
          <div className="top-up-content">
            <h3>Add Funds to Wallet</h3>
            <div className="amount-options">
              {topUpOptions.map(option => (
                <button
                  key={option.value}
                  className={`amount-option ${topUpAmount === option.value ? 'selected' : ''}`}
                  onClick={() => setTopUpAmount(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="custom-amount">
              <label>Custom Amount:</label>
              <input
                type="number"
                min="100"
                max="100000"
                step="100"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(parseInt(e.target.value) || 0)}
                placeholder="Enter amount in cents"
              />
              <span className="amount-display">${formatBalance(topUpAmount)}</span>
            </div>
            {error && <div className="error-message">{error}</div>}
            <div className="top-up-buttons">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowTopUp(false);
                  setError('');
                }}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn"
                onClick={handleTopUp}
                disabled={isProcessing || topUpAmount < 100}
              >
                {isProcessing ? 'Processing...' : 'Add Funds'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="transactions-section">
        <h3>Recent Transactions</h3>
        {transactions.length === 0 ? (
          <p className="no-transactions">No transactions yet</p>
        ) : (
          <div className="transactions-list">
            {transactions.map((transaction, index) => (
              <div key={index} className="transaction-item">
                <div className="transaction-info">
                  <span className="transaction-type">
                    {getTransactionTypeDisplay(transaction.transaction_type)}
                  </span>
                  <span className="transaction-date">
                    {new Date(transaction.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="transaction-amount">
                  {formatAmount(transaction.amount_cents)}
                </div>
                {transaction.description && (
                  <div className="transaction-description">
                    {transaction.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
